import {filter} from '@prismicio/client';
import type {PrismicDocument} from '@prismicio/types';
import usePrismicSearch from '~~/app/composables/content/usePrismicSearch';
import {baseSearchPredicatesByCustomType} from '~~/app/utils/searchPredicates';

export type DocType = 'events' | 'articles' | 'guides' | 'successStories';

export function resolveDocType(parentDoc: PrismicDocument | null | undefined, routeName: string) {
    const docType = parentDoc?.type;
    if (docType === 'homeArticles') return 'articles';
    if (docType === 'homeGuides') return 'guides';
    if (docType === 'homeSuccessStories') return 'successStories';
    if (routeName === 'resources-events-page') return 'events';

    return null;
}

/**
 * Fetches fallback documents
 */
type PrismicSearchParams = Parameters<typeof usePrismicSearch>[0];

const pageTypeMap: Partial<Record<DocType, PrismicSearchParams>> = {
    articles: {type: 'article', ...baseSearchPredicatesByCustomType.article},
    guides: {type: 'guide', ...baseSearchPredicatesByCustomType.guide},
    successStories: {type: 'successStory', ...baseSearchPredicatesByCustomType.successStory},
};

export function fetchFallbackDocuments(docType: DocType, count: number) {
    return async (): Promise<PrismicDocument[]> => {
        if (count < 1) return [];

        if (docType === 'events') {
            const yesterdayDate = new Date();
            yesterdayDate.setDate(yesterdayDate.getDate() - 1);
            const yesterday = yesterdayDate.toISOString().split('T')[0];
            const query = await usePrismicSearch(
                {
                    type: 'event',
                    predicates: [
                        ...(baseSearchPredicatesByCustomType.event.predicates ?? []),
                        filter.dateAfter('my.event.date', yesterday),
                        filter.any('my.event.eventType', ['offline', 'online']),
                    ],
                    orderings: [{field: 'my.event.date', direction: 'asc'}],
                    pageSize: count,
                    page: 1,
                },
                true,
            );
            return query.results;
        }

        const config = pageTypeMap[docType];
        if (!config) return [];

        const query = await usePrismicSearch({...config, pageSize: count, page: 1}, true);
        return query.results;
    };
}
